'use client';

import { AdvancedFilter } from '@cldsystems/e3-fe-lib';
import {
  useCompanyGetById,
  usePriceListGetList,
  WebshopPriceList,
} from '@cldsystems/ws-api-client';
import { useMemo } from 'react';
import { useKeycloak } from '@/providers/keycloak-provider';
import { useStore } from '@/store/zustand/zustand.setup';
import { buildPriceFilterContext, type PriceFilterContext } from '@/utils/effective-product-price';

/**
 * Same inputs as {@link FormattedPrice}: company price lists (non-inactive) and category discounts.
 * React Query dedupes; safe to call from multiple product-page descendants.
 */
export function usePriceFilterContext(): PriceFilterContext {
  const { keycloak } = useKeycloak();
  const companyId = useStore(state => state.company.companyId);
  const isAuthenticatedWithCompany = Boolean(keycloak?.authenticated) && Boolean(companyId);

  const advancedFilter: AdvancedFilter<WebshopPriceList> = {
    AND: [
      {
        OR: [
          {
            'companies.id': {
              operator: 'contains',
              value: companyId ?? '',
            },
          },
        ],
      },
      {
        stage: {
          operator: 'not_equals',
          value: 'Inactive',
        },
      },
    ],
  };

  const { data: priceList } = usePriceListGetList({
    params: { from: '0', size: '100' },
    queries: { advancedFilter: JSON.stringify(advancedFilter) },
    enabled: isAuthenticatedWithCompany,
    staleTime: Infinity,
  });

  const { data: companyData } = useCompanyGetById({
    params: { id: companyId ?? '' },
    enabled: isAuthenticatedWithCompany,
    staleTime: Infinity,
  });

  return useMemo(
    () =>
      buildPriceFilterContext({
        companyCategoryDiscounts: companyData?.company.categoryDiscounts,
        priceListProducts: priceList?.items[0]?.products,
      }),
    [companyData, priceList],
  );
}
